/**
 * Named deterministic capture states.
 *
 * A shot is a seed, an optional command log, a target tick, and a camera
 * position. Determinism makes the capture bit-identical by construction —
 * which is the thing the reference project had to build isolation machinery to
 * approximate, and gets here for free.
 */

import type { MatchDefinition } from '../sim/index.js'
import type { ScheduledCommand } from '../sim/scenario.js'
import { defaultSlots } from '../sim/scenario.js'
import { ID } from '../sim/data/entities.js'

export interface Shot {
  name: string
  match: MatchDefinition
  /** Tick to run to before capturing. */
  tick: number
  /** Camera centre, in cells. Defaults to the map centre. */
  center?: [number, number]
  commands?: ScheduledCommand[]
  /** Enables the palette quantizer, for comparing against the native render. */
  quantize?: boolean

  /**
   * Draws the HUD, and captures the whole page rather than the world canvas.
   *
   * Every other shot photographs the render with the interface switched off, which is
   * right for judging terrain or a mesh and leaves the sidebar as the one component
   * with a reference frame that nothing has ever scored. The HUD is DOM sitting beside
   * the canvas, so a shot that wants it has to be captured differently.
   */
  hud?: boolean
  /**
   * Enables the CRT filter. Off for every other shot on purpose: it curves,
   * tints and dims every pixel, so the colour-discipline and pixel-diff gates
   * would be measuring the filter rather than the render.
   */
  /** A named world-population routine, applied after the tick seek. */
  populate?: string
  /**
   * Whose fog to draw. Shots are observers by default — they are meant to show
   * the render, not the fog — so this opts one in.
   */
  localSlot?: number

  /**
   * A placement footprint to preview, as the player would see it. Legality is
   * evaluated through the real rule, so a shot pinned on a refused placement
   * fails the pixel diff the moment the ghost stops reading as refused.
   */
  ghost?: { kind: number; cx: number; cy: number }
  /**
   * A second capture offset from the first by this many internal pixels.
   * The motion pair (U19) uses 1.5 to assert dither stability under pan —
   * a still-frame gate is structurally blind to dither crawl, which is the
   * characteristic failure of this rendering approach.
   */
  motionOffset?: [number, number]
}

const match = (mapSeed: string, players = 2): MatchDefinition => ({
  mapSeed,
  seed: mapSeed,
  slots: defaultSlots(players),
})

export const SHOTS: Shot[] = [
  {
    /**
     * **This framing clips tall structures and is quietly shaping their design.**
     *
     * The topmost structure pixel sits at y = 0, flush against the frame edge, so a
     * building taller than roughly 1.75 units is cut off. A variant builder found it
     * on its own — "the vertical read had to be paid for in a squat keep rather than
     * a tall core" — which means designs are being shortened to fit a *capture*
     * rather than the game, where the camera follows the action and height is free.
     *
     * Not fixed here because moving `center` invalidates every crop in
     * `tools/preview.mjs` and every close-up rendered against them, and those have to
     * be re-derived by pixel-diff rather than guessed. Do both together.
     */
    name: 'sheet-helios',
    match: match('shot-sheet'),
    tick: 1,
    populate: 'sheet-helios',
    center: [64, 64],
  },
  {
    name: 'sheet-ascendancy',
    match: match('shot-sheet'),
    tick: 1,
    populate: 'sheet-ascendancy',
    center: [64, 64],
  },
  {
    name: 'walker-march',
    match: match('shot-walkers'),
    tick: 48,
    populate: 'walker-march',
    center: [63, 63],
  },
  {
    // A battle in progress: tracers, muzzle flashes, wrecks burning. The one
    // shot that records what the game actually looks like while it is played.
    name: 'battle',
    match: match('shot-battle'),
    // 403 rather than 400: a live detonation with a hot core, rather than the
    // gap between two of them. See the note on `prism-volley` — a still frame
    // of a correctly-ageing effect system has to be aimed.
    tick: 403,
    populate: 'battle',
    center: [64, 62],
  },
  {
    name: 'air-strike',
    match: match('shot-air'),
    // 24, not 1: at tick 1 the strike has not landed. This read as a rich frame
    // only while effects were frozen at spawn and the whole run's particles were
    // stacked on top of each other. See the note on `prism-volley`.
    tick: 24,
    populate: 'air-strike',
    center: [64, 63],
  },
  {
    /**
     * The same air strike, 1.7 seconds later. A **temporal** probe, and the only
     * shot in the set that scores what effects do *not* do.
     *
     * Every other shot photographs an effect at its most flattering instant,
     * which rewards making effects last longer — a builder scored on stills
     * alone raised beam lifetimes from 0.28s to 0.5s and debris from 0.16s to
     * 1.3s, wrote "a beat that lasts four ticks does not exist" in a comment,
     * and scored *higher* while the battlefield filled with white cubes that
     * never cleared. True of a still frame; false of a game.
     *
     * By tick 75 a single strike should have left ground scorch, wreckage, and
     * at most a thin dissipating plume. A field still littered with debris here
     * is a lifetime that was padded to be photographed, and it now costs points
     * instead of earning them.
     */
    name: 'air-strike-settled',
    match: match('shot-air'),
    tick: 75,
    populate: 'air-strike',
    center: [64, 63],
  },
  {
    /**
     * The interface, over a base far enough along to fill it: credits ticking, power
     * drawn, the build list populated, the radar showing something.
     */
    name: 'hud-base',
    match: match('shot-hud'),
    tick: 240,
    populate: 'early-base',
    center: [26, 24],
    localSlot: 0,
    hud: true,
  },
  {
    // A footprint outside the build area. Every tile must read red: the whole
    // ghost once stayed green here, because the tint was derived from per-cell
    // passability instead of from the placement verdict, and nothing about an
    // out-of-area cell is impassable.
    name: 'placement-refused',
    match: match('shot-place'),
    tick: 1,
    populate: 'early-base',
    center: [26, 24],
    ghost: { kind: ID.CommsMast, cx: 30, cy: 24 },
  },
  {
    name: 'terrain-overview',
    match: match('shot-terrain'),
    tick: 1,
    center: [64, 64],
  },
  {
    name: 'terrain-corner',
    match: match('shot-terrain'),
    tick: 1,
    center: [24, 24],
  },
  {
    name: 'terrain-quantized',
    match: match('shot-terrain'),
    tick: 1,
    center: [64, 64],
    quantize: true,
  },
  {
    name: 'regolith-field',
    match: match('mars'),
    tick: 1,
    center: [30, 30],
  },
  {
    // A gem patch inside an ordinary ore field, so the two can be compared.
    // Gems are worth several times as much and are deliberately scarce.
    name: 'gem-field',
    match: match('mars'),
    tick: 1,
    center: [26, 22],
  },
  {
    // The opening state: a founder alone on the map, which is what a player
    // actually sees first and therefore the frame that has to read.
    name: 'early-base',
    match: match('shot-base'),
    tick: 30,
    center: [18, 18],
    populate: 'early-base',
  },
  {
    name: 'mid-battle',
    match: match('shot-battle'),
    tick: 60,
    center: [64, 64],
    populate: 'battle',
  },
  {
    name: 'fog-boundary',
    match: match('shot-fog'),
    tick: 40,
    center: [40, 40],
    populate: 'fog',
    // Captured from a player's seat rather than an observer's, because the
    // whole point of this shot is what a player cannot see.
    localSlot: 0,
  },
  {
    // A prism volley: the shot beams and the links feeding them. The mechanic
    // has been in the simulation since the tier-three pass and nothing drew
    // it, so a player had no way to know their tanks were combining.
    name: 'prism-volley',
    match: match('shot-prism'),
    // Tick 14, not 40. Effects used to be frozen at spawn — the harness seeks
    // hundreds of ticks and renders one frame, and nothing aged particles — so
    // every beam ever fired was still on screen whenever you photographed it.
    // Now that they expire properly, a 0.28s beam fired around tick 10 is long
    // gone by 40, and this shot caught only its impact. 14 is where the link
    // beams between the combining tanks are actually lit.
    tick: 14,
    center: [64, 64],
    populate: 'prism',
  },
  {
    // Three squads in the open beside three dug in, so the fieldworks can be
    // compared against the thing they are meant to protect.
    name: 'digin',
    match: match('shot-digin'),
    tick: 3,
    center: [64, 64],
    populate: 'digin',
  },
  {
    // Every ring-mount fitting side by side, both factions: the passenger
    // must be readable from the silhouette alone.
    name: 'ifv',
    match: match('shot-ifv'),
    tick: 3,
    center: [64, 64],
    populate: 'ifv',
  },
  {
    name: 'roster-helios',
    match: match('shot-roster'),
    tick: 5,
    center: [30, 30],
    populate: 'roster-helios',
  },
  {
    name: 'roster-ascendancy',
    match: match('shot-roster'),
    tick: 5,
    center: [30, 30],
    populate: 'roster-ascendancy',
  },
  {
    name: 'stress-400',
    match: match('shot-stress'),
    tick: 20,
    center: [64, 64],
    populate: 'stress',
  },
  {
    // The motion pair. Captured 1.5 internal pixels apart: the two frames must
    // differ only where geometry moved, never in the dither pattern across
    // static surfaces. Every other shot is a still, and stills are structurally
    // blind to dither crawl.
    name: 'motion-pair',
    match: match('shot-terrain'),
    tick: 1,
    center: [64, 64],
    motionOffset: [1.5, 0],
  },
]

export function shotByName(name: string): Shot | undefined {
  return SHOTS.find((s) => s.name === name)
}

export function shotNames(): string[] {
  return SHOTS.map((s) => s.name)
}
